import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { getWorksheet, aiSolutions } from "@/lib/api";
import { Printer, ArrowLeft, Loader2, Sparkles } from "lucide-react";
import { QuestionText } from "@/components/QuestionText";
import MathText from "@/components/MathText";
import { WorksheetHeader, InstructionRibbon, SectionBanner, WorksheetFooter } from "@/components/WorksheetTheme";

export default function Print() {
  const { id } = useParams();
  const [ws, setWs] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const [showHints, setShowHints] = useState(true);
  const [solutions, setSolutions] = useState(null);
  const [loadingSols, setLoadingSols] = useState(false);

  useEffect(() => {
    getWorksheet(id).then(setWs);
  }, [id]);

  const fetchSolutions = async () => {
    setLoadingSols(true);
    try {
      const r = await aiSolutions(id);
      setSolutions(r.solutions);
      setShowAnswers(true);
      toast.success(r.from_cache ? "Loaded cached solutions." : "Solutions ready!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate solutions");
    } finally {
      setLoadingSols(false);
    }
  };

  if (!ws) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const canShowKey = showAnswers;
  const CHOICE_LABELS = ["A", "B", "C", "D", "E"];
  const gradeLabel = ws.grade === "K" ? "Kindergarten" : `Grade ${ws.grade}`;
  const withChoices = ws.problems.filter((p) => p.choices && p.choices.length).length;
  const secTitle = withChoices === ws.problems.length ? "Multiple Choice"
    : withChoices === 0 ? "Solve Each Item" : "Answer Each Item";
  const secInstr = withChoices === ws.problems.length ? "Choose the letter of the correct answer."
    : withChoices === 0 ? "Write your answer on the line provided."
    : "Choose or write the correct answer for each item.";

  return (
    <div className="min-h-screen">
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
      <div className="no-print sticky top-0 z-30 bg-[#F9F9F6] border-b-[3px] border-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <Link to={`/practice/${id}`} className="brutal-btn bg-white px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="back-button">
            <ArrowLeft strokeWidth={2.5} className="w-4 h-4" /> Back
          </Link>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowHints((v) => !v)}
              className="brutal-btn bg-white px-4 py-2 text-sm"
              data-testid="toggle-hints"
            >
              {showHints ? "Hide hints" : "Show hints"}
            </button>
            <button
              onClick={() => setShowAnswers((v) => !v)}
              className="brutal-btn bg-brand-yellow px-4 py-2 text-sm inline-flex items-center gap-2"
              data-testid="toggle-answers"
            >
              {showAnswers ? "Hide answers" : "Show answer key"}
            </button>
            <button
              onClick={fetchSolutions}
              disabled={loadingSols}
              className="brutal-btn bg-brand-coral px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="ai-solutions-button"
            >
              {loadingSols ? <Loader2 strokeWidth={2.5} className="w-4 h-4 animate-spin" /> : <Sparkles strokeWidth={2.5} className="w-4 h-4" />}
              {loadingSols ? "Generating..." : solutions ? "Solutions loaded" : "AI Solutions"}
            </button>
            <button
              onClick={() => window.print()}
              className="brutal-btn bg-brand-blue text-white px-4 py-2 text-sm inline-flex items-center gap-2"
              data-testid="print-trigger"
            >
              <Printer strokeWidth={2.5} className="w-4 h-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <div className="bg-white border-[3px] border-[#0A0A0A] rounded-2xl p-8 print-page shadow-brutal">
          <WorksheetHeader
            title={ws.title}
            eyebrow="Worksheet"
            subtitle={`${gradeLabel} · ${ws.problems.length} ${ws.problems.length === 1 ? "item" : "items"}`}
          />
          <InstructionRibbon>Read each item carefully and answer to the best of your ability.</InstructionRibbon>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm border-y-[3px] border-[#0A0A0A]/10 py-3 mb-5 font-bold">
            <p className="flex items-baseline gap-2">
              Name:<span className="inline-block flex-1 min-w-[8rem] border-b-2 border-[#0A0A0A]/55">&nbsp;</span>
            </p>
            <p className="flex items-baseline gap-2">
              Score:<span className="inline-block w-14 border-b-2 border-[#0A0A0A]/55">&nbsp;</span>/ {ws.problems.length}
            </p>
            <p className="flex items-baseline gap-2">
              Grade &amp; Section:<span className="inline-block flex-1 min-w-[5rem] border-b-2 border-[#0A0A0A]/55">&nbsp;</span>
            </p>
            <p className="flex items-baseline gap-2">
              Date:<span className="inline-block flex-1 min-w-[7rem] border-b-2 border-[#0A0A0A]/55">&nbsp;</span>
            </p>
          </div>
          <SectionBanner index={0} title={secTitle} points={ws.problems.length} instructions={secInstr} />
          <div className="space-y-6">
            {ws.problems.map((p, idx) => {
              const sol = solutions?.[p.id];
              return (
                <div key={p.id} className="flex gap-3 break-inside-avoid" data-testid={`print-problem-${p.id}`}>
                  <span className="flex-shrink-0 w-6 font-mono font-bold text-base mt-0.5">{idx + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <QuestionText text={p.question} size="base" />
                    {p.figure_svg && (
                      <div className="my-2" dangerouslySetInnerHTML={{ __html: p.figure_svg }} />
                    )}
                    {p.choices && (
                      <ul className="mt-3 grid grid-cols-1 gap-1.5 text-sm list-none p-0 m-0">
                        {p.choices.map((choice, ci) => (
                          <li key={ci} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-[#0A0A0A]/15 bg-[#F9F9F6]">
                            <span className="font-bold bg-white border-2 border-[#0A0A0A] rounded-md w-[22px] h-[22px] flex items-center justify-center text-xs flex-shrink-0">{CHOICE_LABELS[ci] ?? ci + 1}</span>
                            <span><MathText>{choice}</MathText></span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {canShowKey && p.answer !== undefined && (
                      <span className="text-brand-blue font-bold text-sm ml-1 mt-1 block">Answer: <MathText>{p.answer}</MathText></span>
                    )}
                    {showHints && p.hint && (
                      <div className="text-xs italic font-body text-[#1a1a1a] mt-1">💡 <MathText>{p.hint}</MathText></div>
                    )}
                    {canShowKey && sol && (
                      <div className="mt-2 p-3 bg-brand-yellow/30 border-2 border-dashed border-[#0A0A0A] rounded-lg text-xs font-body leading-relaxed flex flex-col gap-1">
                        {(() => {
                          const lines = (sol.includes("\n")
                            ? sol
                            : sol.replace(/(Why:|How:|Conclusion:|Step\s+\d+:|Answer:)/g, "\n$1")
                          ).split("\n").map(l => l.trim()).filter(l => l.length > 0);
                          return lines.map((line, li) => {
                            const numbered = /^(\d+\.\s*[^:]{1,60}:)\s*(.*)$/.exec(line);
                            if (numbered) {
                              const [, header, rest] = numbered;
                              return (
                                <div key={li} className={li > 0 ? "mt-1" : ""}>
                                  <span className="font-bold"><MathText>{header}</MathText></span>
                                  {rest && <> <MathText>{rest}</MathText></>}
                                </div>
                              );
                            }
                            const isHeader = /^(How:|Why:|Conclusion:)/.test(line);
                            return (
                              <div key={li} className={isHeader ? "font-bold mt-1" : ""}>
                                <MathText>{line}</MathText>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <WorksheetFooter />
          <p className="mt-2 text-[11px] text-center text-muted-foreground">
            Generated {new Date(ws.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
}
